import type { Catalog } from '../types';

/**
 * As poucas palavras que a formatação precisa.
 *
 * Os meses ficam numa **tabela à mão**, e não em `Intl.DateTimeFormat({month:'short'})`, porque o
 * ICU emite `"jul."` com ponto em algumas versões e `"jul"` em outras — e a largura renderizada
 * importa no layout do canvas. Uma tabela é previsível.
 *
 * Data com o **dia primeiro nos dois idiomas** (`19 jul 2026` / `19 Jul 2026`): o mês é palavra, o
 * que torna a ordem inequívoca, e evita escolher entre variantes regionais do inglês.
 */
export const format = {
  'format.months': {
    pt: 'jan fev mar abr mai jun jul ago set out nov dez',
    en: 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec',
  },
  /**
   * Os sufixos ordinais, um por categoria do `Intl.PluralRules` em modo ordinal. Português resolve
   * tudo em `other`; inglês usa as quatro. Ficam como chaves separadas, e não como entrada plural,
   * porque a categoria aqui vem do **ordinal** e não do cardinal que o `t` consulta.
   */
  'format.ordinal.one': { pt: 'º', en: 'st' },
  'format.ordinal.two': { pt: 'º', en: 'nd' },
  'format.ordinal.few': { pt: 'º', en: 'rd' },
  'format.ordinal.other': { pt: 'º', en: 'th' },

  /** "6 de 18" — a amostra por trás de uma porcentagem. */
  'format.outOf': { pt: '{hit} de {total}', en: '{hit} of {total}' },

  'format.today': { pt: 'hoje, {time}', en: 'today, {time}' },
  'format.yesterday': { pt: 'ontem, {time}', en: 'yesterday, {time}' },

  /**
   * O subtítulo dos gráficos. Uma chave por ramo, e não fragmentos concatenados: em português o ano
   * vem depois do mês com "de" no meio, em inglês vem colado — ordem que só a frase inteira carrega.
   */
  'format.range.sameMonth': { pt: '{month} de {year}', en: '{month} {year}' },
  'format.range.sameYear': { pt: '{from} a {to} de {year}', en: '{from} to {to} {year}' },
  'format.range.acrossYears': {
    pt: '{from} de {fromYear} a {to} de {toYear}',
    en: '{from} {fromYear} to {to} {toYear}',
  },

  /**
   * `jd` é a abreviação de *jarda*; quem lê inglês vê `418 jd` como erro de digitação. Já `m` é
   * símbolo do SI e não muda de idioma — mas continua sendo chave, para o par ficar visível.
   */
  'format.unit.yd': { pt: 'jd', en: 'yd' },
  'format.unit.m': { pt: 'm', en: 'm' },
  'format.unit.km': { pt: 'km', en: 'km' },
} as const satisfies Catalog;
